import { getOverlayPortalTarget } from "@/components/modal/overlay-dom";
/**
 * Copy text to the clipboard, tolerating environments where the async Clipboard
 * API is unavailable (non-secure contexts, older browsers) — reading
 * `navigator.clipboard.writeText` directly would throw synchronously there,
 * before any `.catch` could attach. Prefer the async API, then fall back to a
 * hidden-textarea `execCommand('copy')`, and finally report failure.
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText != null) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the execCommand path below.
    }
  }

  if (typeof document === "undefined") return false;
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    // Keep it out of view and non-interactive while still selectable.
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.setAttribute("readonly", "");
    getOverlayPortalTarget(document.activeElement ?? document.body).appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    textarea.remove();
    return ok;
  } catch {
    return false;
  }
};

/**
 * The absolute URL for an app-relative href, so "Copy link" puts a shareable
 * link on the clipboard rather than a bare path. Falls back to the relative
 * href when there is no window (SSR); callers only render client-side in
 * practice.
 */
export const absoluteAppUrl = (href: string): string => {
  if (typeof window === "undefined") return href;
  return new URL(href, window.location.origin).toString();
};
